import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Target, Heart, Users, Award, MapPin, Phone, Mail, Leaf, Shield, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutUs = () => {
  const values = [
    { icon: Heart, title: 'Quality First', desc: 'We never compromise on the quality of our products or customer service.' },
    { icon: Users, title: 'Customer Focused', desc: 'Every decision we make starts with how it benefits our customers.' },
    { icon: Leaf, title: 'Sustainability', desc: 'We\'re committed to reducing food waste and promoting sustainable living.' },
    { icon: Shield, title: 'Reliability', desc: 'Our machines are built to last, backed by industry-leading warranties.' }
  ];

  const milestones = [
    { year: '2018', title: 'Founded', desc: 'HomeLyph was born from a passion for food preservation and self-sufficiency.' },
    { year: '2019', title: 'First Product', desc: 'Launched our flagship Pro 500 freeze dryer to overwhelming response.' },
    { year: '2021', title: '10,000 Customers', desc: 'Reached a milestone of 10,000 happy customers across North America.' },
    { year: '2023', title: 'Commercial Line', desc: 'Expanded into commercial-grade freeze dryers for businesses.' },
    { year: '2024', title: 'Innovation Award', desc: 'Recognized for innovation in home food preservation technology.' },
    { year: '2025', title: 'Global Expansion', desc: 'Began shipping internationally, bringing freeze drying to the world.' }
  ];

  const team = [
    { name: 'Sarah Chen', role: 'CEO & Founder', image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400', bio: 'Former food scientist with 15 years in preservation technology.' },
    { name: 'Michael Torres', role: 'Head of Engineering', image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400', bio: 'Led development of our patented Smart Dry technology.' },
    { name: 'Emily Watson', role: 'Customer Success', image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400', bio: 'Dedicated to making every customer a freeze drying expert.' }
  ];

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />

      {/* Hero */}
      <div className="pt-44 pb-20 bg-gradient-to-br from-[#003456] via-[#002845] to-[#001d30] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#fdc31c] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#fdc31c] font-semibold text-sm uppercase tracking-wider">About HomeLyph</span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
                Preserving Food, <br />Empowering Lives
              </h1>
              <p className="text-xl text-white/80 mb-8">
                We're on a mission to help families achieve food independence through innovative freeze drying technology. Since 2018, we've helped over 25,000 customers preserve their harvests, reduce food waste, and prepare for the future.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/products">
                  <Button className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203]">
                    Explore Products
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456]">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <img 
                src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600" 
                alt="HomeLyph Team"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mission */}
      <div className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=600" 
                alt="Freeze dried food"
                className="rounded-2xl shadow-lg"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#003456] rounded-xl flex items-center justify-center">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <span className="text-[#003456] font-semibold uppercase tracking-wider text-sm">Our Mission</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-6">
                Making Food Preservation Accessible to Everyone
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                We believe everyone deserves access to healthy, long-lasting food. Our mission is to bring commercial-grade freeze drying technology into homes at an affordable price, empowering families to take control of their food supply.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                Whether you're a gardener looking to preserve your harvest, a parent wanting healthier snacks for your kids, or someone preparing for emergencies—we're here to help you succeed.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">Our Values</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              These principles guide everything we do at HomeLyph
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <div key={i} className="bg-[#f0f4f8] rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#003456] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <value.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-bold text-[#0a0203] mb-2">{value.title}</h3>
                <p className="text-gray-600 text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">Our Journey</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              From a small startup to an industry leader
            </p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-[#003456]/20 hidden md:block"></div>
            <div className="space-y-8">
              {milestones.map((milestone, i) => (
                <div key={i} className={`flex items-center gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow inline-block">
                      <span className="text-[#fdc31c] font-bold text-2xl">{milestone.year}</span>
                      <h3 className="font-bold text-[#0a0203] mt-1">{milestone.title}</h3>
                      <p className="text-gray-600 text-sm mt-2">{milestone.desc}</p>
                    </div>
                  </div>
                  <div className="hidden md:flex w-4 h-4 bg-[#003456] rounded-full relative z-10"></div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Team */}
      <div className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">Meet Our Team</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              The passionate people behind HomeLyph
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, i) => (
              <div key={i} className="bg-[#f0f4f8] rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-square overflow-hidden">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-6 text-center">
                  <h3 className="font-bold text-[#0a0203] text-lg">{member.name}</h3>
                  <p className="text-[#003456] font-medium text-sm mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-gradient-to-r from-[#003456] to-[#001d30] rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h2 className="text-2xl md:text-3xl font-bold mb-4">Get in Touch</h2>
                <p className="text-white/80 mb-6">
                  Have questions? We'd love to hear from you. Our team is always ready to help.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-[#fdc31c]" />
                    <span>Calgary, Alberta, Canada</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-[#fdc31c]" />
                    <span>1-800-HOMELYPH</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-[#fdc31c]" />
                    <span>hello@homelyph.com</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
                <Link to="/contact">
                  <Button className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203] w-full sm:w-auto">
                    Contact Us
                  </Button>
                </Link>
                <Link to="/customer-support">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456] w-full sm:w-auto">
                    Support Center
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutUs;
